/**
 * FlahaSOIL v2 — soil-test wizard defaults and step metadata.
 *
 * Test-level field expectations come from the project standard in
 * `@flaha/shared-types` (`SOIL_TEST_LEVEL_STANDARD`). This file only
 * shapes wizard steps and form field widgets.
 */
import {
	SoilTestLevel,
	SOIL_TEST_LEVEL_STANDARD,
	captureSummaryForLevel,
	getSoilTestLevelStandard,
} from "@flaha/shared-types";

export type WizardStepKey =
	| "sample-info"
	| "test-level"
	| "preliminary"
	| "moderate"
	| "advanced"
	| "review";

export interface WizardStepMeta {
	key: WizardStepKey;
	label: string;
	/**
	 * Short caption rendered under the step label. Helps a first-time
	 * user understand what each step asks for without opening it.
	 */
	description?: string;
}

export const ALL_WIZARD_STEPS: WizardStepMeta[] = [
	{
		key: "sample-info",
		label: "Sample Info",
		description: "Project, location, and depth of the sample.",
	},
	{
		key: "test-level",
		label: "Test Level",
		description: "Choose how much detail your lab report contains.",
	},
	{
		key: "preliminary",
		label: "Texture & Basics",
		description: "Sand / silt / clay, organic matter, pH, salinity.",
	},
	{
		key: "moderate",
		label: "Core Chemistry",
		description: "Major cations, CEC, and macronutrients.",
	},
	{
		key: "advanced",
		label: "Advanced Panel",
		description: "Micronutrients and salinity indices.",
	},
	{
		key: "review",
		label: "Review & Submit",
		description: "Confirm everything before sending to the lab engine.",
	},
];

/**
 * Visible steps for the given test level.
 *
 * - PRELIMINARY → sample, level, preliminary, review
 * - MODERATE    → sample, level, preliminary, moderate, review
 * - ADVANCED    → sample, level, preliminary, moderate, advanced, review
 */
export function visibleStepsForLevel(
	level: SoilTestLevel
): WizardStepMeta[] {
	const showModerate =
		level === SoilTestLevel.MODERATE || level === SoilTestLevel.ADVANCED;
	const showAdvanced = level === SoilTestLevel.ADVANCED;

	return ALL_WIZARD_STEPS.filter((step) => {
		if (step.key === "moderate" && !showModerate) return false;
		if (step.key === "advanced" && !showAdvanced) return false;
		return true;
	});
}

export interface TestLevelOption {
	value: SoilTestLevel;
	label: string;
	description: string;
	/** Expected fields at this level (project standard). */
	captures: string;
	bestFor: string;
	/** Fields newly required at this level only. */
	addedFieldLabels: string[];
}

/** Wizard cards — copy and field lists follow SOIL_TEST_LEVEL_STANDARD. */
export const TEST_LEVEL_OPTIONS: TestLevelOption[] = SOIL_TEST_LEVEL_STANDARD.map(
	(std) => ({
		value: std.level,
		label: std.label,
		description: std.summary,
		captures: captureSummaryForLevel(std.level),
		bestFor: std.bestFor,
		addedFieldLabels: std.addedFields.map((f) => f.label),
	})
);

export { getSoilTestLevelStandard, captureSummaryForLevel };

export interface FieldMeta {
	key: string;
	label: string;
	unit?: string;
	helperText?: string;
}

export interface FieldGroup {
	title: string;
	caption?: string;
	fields: FieldMeta[];
}

export const PRELIMINARY_GROUPS: FieldGroup[] = [
	{
		title: "Texture",
		caption:
			"Particle-size distribution. Sand + silt + clay should sum to 100 %.",
		fields: [
			{ key: "sandPercent", label: "Sand", unit: "%" },
			{ key: "siltPercent", label: "Silt", unit: "%" },
			{ key: "clayPercent", label: "Clay", unit: "%" },
		],
	},
	{
		title: "Organic matter",
		caption: "Loss-on-ignition or Walkley-Black, dry-weight basis.",
		fields: [
			{ key: "organicMatterPercent", label: "Organic matter", unit: "%" },
		],
	},
	{
		title: "Reaction & salinity",
		caption: "pH (1:1 water) and saturated-paste EC / TDS.",
		fields: [
			{ key: "pH", label: "pH" },
			{ key: "ecDsM", label: "EC", unit: "dS/m" },
			{ key: "tdsMgL", label: "TDS", unit: "mg/L" },
		],
	},
];

export const MODERATE_GROUPS: FieldGroup[] = [
	{
		title: "Exchangeable cations",
		caption: "Saturated-paste or ammonium-acetate extract.",
		fields: [
			{ key: "ca", label: "Calcium (Ca)", unit: "cmol(+)/kg" },
			{ key: "mg", label: "Magnesium (Mg)", unit: "cmol(+)/kg" },
			{ key: "k", label: "Potassium (K)", unit: "cmol(+)/kg" },
			{ key: "na", label: "Sodium (Na)", unit: "cmol(+)/kg" },
		],
	},
	{
		title: "Macronutrients & anions",
		caption: "Plant-available pools by standard lab methods.",
		fields: [
			{ key: "n", label: "Nitrogen (N)", unit: "mg/kg" },
			{ key: "p", label: "Phosphorus (P)", unit: "mg/kg" },
			{ key: "cl", label: "Chloride (Cl)", unit: "mg/kg" },
		],
	},
	{
		title: "CEC",
		caption: "Leave blank to derive from the cations above.",
		fields: [{ key: "cec", label: "CEC", unit: "cmol(+)/kg" }],
	},
];

export const ADVANCED_GROUPS: FieldGroup[] = [
	{
		title: "Micronutrients + S",
		caption:
			"Plant-available pools (typical DTPA). Mo and S are part of the ADVANCED evidence standard.",
		fields: [
			{ key: "fe", label: "Iron (Fe)", unit: "mg/kg" },
			{ key: "mn", label: "Manganese (Mn)", unit: "mg/kg" },
			{ key: "zn", label: "Zinc (Zn)", unit: "mg/kg" },
			{ key: "cu", label: "Copper (Cu)", unit: "mg/kg" },
			{ key: "b", label: "Boron (B)", unit: "mg/kg" },
			{ key: "mo", label: "Molybdenum (Mo)", unit: "mg/kg" },
			{ key: "s", label: "Sulphur (S)", unit: "mg/kg" },
		],
	},
	{
		title: "Salinity & sodicity indices",
		caption: "Carbonate fractions and lab SAR/ESP (ADVANCED evidence standard).",
		fields: [
			{ key: "carbonate", label: "Carbonate", unit: "mg/kg" },
			{ key: "bicarbonate", label: "Bicarbonate", unit: "mg/kg" },
			{ key: "sar", label: "SAR" },
			{ key: "esp", label: "ESP", unit: "%" },
		],
	},
];

/**
 * ADVANCED also expects a heavy-metals / extra analyte panel
 * (`heavyMetalsJson`) for full evidence honesty. Upload via API or lab
 * JSON; not a single scalar form field in the wizard.
 */
export const ADVANCED_JSON_EXTRAS_NOTE =
	"ADVANCED full coverage also expects a heavy-metals / extra panel (heavyMetalsJson) when the laboratory reports it.";

/** Flat field lists kept for backward compatibility with any consumer
 * that iterates fields without caring about grouping. */
export const PRELIMINARY_FIELDS: FieldMeta[] = PRELIMINARY_GROUPS.flatMap(
	(g) => g.fields
);
export const MODERATE_FIELDS: FieldMeta[] = MODERATE_GROUPS.flatMap(
	(g) => g.fields
);
export const ADVANCED_FIELDS: FieldMeta[] = ADVANCED_GROUPS.flatMap(
	(g) => g.fields
);
